import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, ChevronDown } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { es, enUS } from 'date-fns/locale';
import { Button } from '@/shared/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu';
import { PERIOD_TYPE } from '../types';
import type { PeriodType } from '../types';

interface PeriodSelectorProps {
  periodType: PeriodType;
  periodStart: string;
  periodEnd: string;
  onNavigate: (direction: 'prev' | 'next') => void;
  onChangePeriodType: (type: PeriodType) => void;
}

function useDateLocale() {
  const { i18n } = useTranslation();
  return i18n.language.startsWith('es') ? es : enUS;
}

export function PeriodSelector({
  periodType,
  periodStart,
  periodEnd,
  onNavigate,
  onChangePeriodType,
}: PeriodSelectorProps) {
  const { t } = useTranslation('laborPerformance');
  const locale = useDateLocale();

  const startDate = parseISO(periodStart);
  const endDate = parseISO(periodEnd);

  const rangeLabel = `${format(startDate, 'd MMM', { locale })} – ${format(endDate, 'd MMM', { locale })}`;

  const PERIOD_LABELS: Record<PeriodType, string> = {
    [PERIOD_TYPE.WEEK]: t('period.weekly'),
    [PERIOD_TYPE.MONTH]: t('period.monthly'),
    [PERIOD_TYPE.QUARTER]: t('period.quarterly'),
  };

  return (
    <div data-testid="labor-performance-period-selector" className="flex items-center gap-2">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="gap-1.5">
            {PERIOD_LABELS[periodType]}
            <ChevronDown className="h-3.5 w-3.5" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {Object.values(PERIOD_TYPE).map((type) => (
            <DropdownMenuItem key={type} onClick={() => onChangePeriodType(type)}>
              {PERIOD_LABELS[type]}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => onNavigate('prev')}
            aria-label={t('period.prevLabel')}
            data-testid="labor-performance-period-prev-btn"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>{t('period.prevLabel')}</TooltipContent>
      </Tooltip>

      <span className="min-w-[100px] text-center text-sm font-medium">{rangeLabel}</span>

      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => onNavigate('next')}
            aria-label={t('period.nextLabel')}
            data-testid="labor-performance-period-next-btn"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>{t('period.nextLabel')}</TooltipContent>
      </Tooltip>
    </div>
  );
}
